// Techmate Cyber Security & Compliance Check — data-driven content.
// All wording, options, weights, controls, risks, actions and compliance rules
// live here so they can be edited without touching logic.

export const AREAS = {
  leadership: { name: "Leadership & Oversight", nist: "GOVERN", peer: 46 },
  knowing: { name: "Knowing What You Have", nist: "IDENTIFY", peer: 48 },
  protecting: { name: "Protecting the Essentials", nist: "PROTECT", peer: 54 },
  spotting: { name: "Spotting Trouble Early", nist: "DETECT", peer: 41 },
  handling: { name: "Handling Incidents", nist: "RESPOND", peer: 39 },
  bouncing: { name: "Bouncing Back", nist: "RECOVER", peer: 50 },
  personalData: { name: "Looking After Personal Data", nist: "DPDP", peer: 40 },
  industry: { name: "Industry & Compliance", nist: "", peer: 42 },
};

// ---------- Section A: Business profile (not scored) ----------
export const PROFILE_QUESTIONS = [
  {
    id: "A1",
    question: "Which sector best describes your business?",
    options: [
      "Manufacturing / industrial",
      "Financial services / lending",
      "Healthcare",
      "Retail / e-commerce",
      "Professional services",
      "IT / software / SaaS",
      "Other",
    ],
  },
  {
    id: "A1b",
    conditional: (a) => a.A1 === "Financial services / lending",
    question: "Is your business connected to the securities market — broker, mutual fund, investment firm?",
    options: ["Yes", "No"],
  },
  {
    id: "A2",
    question: "How many people does your business employ?",
    options: ["1–20", "21–100", "101–500", "500+"],
  },
  {
    id: "A3",
    question: "What is the most sensitive information your business holds?",
    options: [
      "Business records only",
      "Staff & supplier data",
      "Customer personal or payment data",
      "Regulated or highly sensitive data",
    ],
  },
  {
    id: "A4",
    question: "Has your business experienced a security incident in the past two years?",
    options: ["No", "Unsure", "Yes — minor", "Yes — significant"],
  },
];

// ---------- Core: 12 scored questions ----------
export const CORE_QUESTIONS = [
  {
    id: "Q1", area: "leadership", weight: 2, screen: 1,
    question: "Who holds day-to-day responsibility for security in your business?",
    options: ["No one assigned", "Handled informally by IT", "Part-time owner, reactive", "Clear owner, defined duties", "Board-level oversight, reviewed regularly"],
    control: "Security ownership & accountability",
    risk: "Without clear ownership, security gaps go unnoticed and unowned until they cause harm.",
    action: "Establish defined security ownership with a regular leadership review rhythm.",
  },
  {
    id: "Q2", area: "knowing", weight: 2, screen: 1, verify: true,
    question: "How complete is your view of the systems, accounts and data your business holds?",
    options: ["No inventory", "Would need to reconstruct", "Informal, partial list", "Current inventory maintained", "Tracked with owners, reviewed"],
    control: "Asset, account & data inventory",
    risk: "You cannot protect what you do not know you hold; unmanaged systems and forgotten accounts are a primary route in.",
    action: "Establish and maintain an inventory of systems, accounts and data locations with assigned owners.",
  },
  {
    id: "Q3", area: "knowing", weight: 2, screen: 1, verify: true,
    question: "How do you assess the security of the vendors and software you depend on?",
    options: ["Never assessed", "Assumed, not verified", "Key vendors, informal check", "Reviewed before and during use", "Formally assessed and monitored"],
    control: "Third-party & supply-chain risk",
    risk: "A significant share of breaches now arrive through a trusted supplier or third-party tool that was never assessed.",
    action: "Introduce proportionate security due diligence for key suppliers and cloud services.",
  },
  {
    id: "Q4", area: "protecting", weight: 3, screen: 2, verify: true,
    question: "How is access to your important systems verified?",
    options: ["Shared passwords", "Individual passwords only", "Extra verification on some systems", "Extra verification on all key systems", "Enforced everywhere, role-based access"],
    control: "Identity & access control",
    risk: "Credential compromise remains the most common cause of breach; without a second verification factor, one exposed password can open everything.",
    action: "Enforce multi-factor authentication across all business-critical systems and align access to role.",
  },
  {
    id: "Q5", area: "protecting", weight: 2, screen: 2, verify: true,
    question: "How are company devices and software managed — and how quickly is access withdrawn when someone leaves?",
    options: ["Unmanaged; accounts linger", "Basic protection; inconsistent updates", "Most devices covered; manual removal", "All managed; removal on exit", "Centrally managed; automated removal"],
    control: "Endpoint management, patching & joiner-leaver control",
    risk: "Unpatched devices and active accounts belonging to former staff are two of the most frequently exploited weaknesses in any business.",
    action: "Centrally manage and patch all devices, and make access withdrawal an automatic, verified part of exit.",
  },
  {
    id: "Q6", area: "protecting", weight: 2, screen: 2,
    question: "How prepared are your staff to recognise a phishing or scam email?",
    options: ["No training provided", "No guidance; staff self-reliant", "Occasional reminders", "Regular training", "Training plus simulated phishing tests"],
    control: "Security awareness & human factor",
    risk: "The majority of successful attacks begin by deceiving a person; untrained staff are the most accessible target in any organisation.",
    action: "Deliver regular, practical awareness training reinforced with simulated phishing exercises.",
  },
  {
    id: "Q7", area: "spotting", weight: 2, screen: 3, verify: true,
    question: "What visibility do you have of suspicious activity — and of what occurred after an incident?",
    options: ["No visibility", "Might be noticed; no records", "Periodic checks; partial records", "Alerting in place; key records retained", "24/7 monitoring; full records retained"],
    control: "Monitoring, alerting & log retention",
    risk: "Attackers frequently remain undetected for weeks. Without monitoring, a breach becomes apparent only once it is public — and without retained records you cannot establish what was taken, close the gap, or meet the evidence CERT-In expects.",
    action: "Implement continuous monitoring with alerting, and centralise and retain security logs.",
  },
  {
    id: "Q8", area: "handling", weight: 2, screen: 3,
    question: "How prepared is your business to respond to a serious cyber attack?",
    options: ["No plan", "Informally understood by a few", "Documented, untested", "Documented, roles assigned", "Tested regularly, kept current"],
    control: "Incident response plan & roles",
    risk: "In a live incident the first hours determine the outcome; without a rehearsed plan, delay and confusion multiply both damage and cost.",
    action: "Develop and rehearse an incident response plan with clearly assigned roles.",
  },
  {
    id: "Q9", area: "handling", weight: 2, screen: 3,
    question: "How well do you understand your breach-notification obligations?",
    options: ["Obligation unknown", "Known vaguely; no process", "Understood; no defined steps", "Contacts and duties identified", "Documented and rehearsed"],
    control: "Regulatory & customer notification",
    risk: "CERT-In requires cyber incidents to be reported within 6 hours, and the DPDP Act requires notifying the Data Protection Board and affected individuals. Missed deadlines attract penalties in addition to the breach itself.",
    action: "Map your notification obligations and prepare contacts, decision steps and templates in advance.",
  },
  {
    id: "Q10", area: "bouncing", weight: 3, screen: 4, verify: true,
    question: "How confident are you that critical data could be fully recovered — and when was this last tested?",
    options: ["Recovery uncertain", "Copies exist, never tested", "Backed up; untested over a year", "Restored successfully within last year", "Scheduled restore testing, verified"],
    control: "Backup & proven recoverability",
    risk: "An untested backup is an assumption, not a safeguard. Many businesses discover their backups do not restore only after an attack, when it is too late to act.",
    action: "Implement reliable backups and prove recoverability through scheduled, documented restore testing.",
  },
  {
    id: "Q11", area: "bouncing", weight: 3, screen: 4, verify: true,
    question: "Could an attacker who reached your systems also compromise your backups?",
    options: ["Backups on same network", "Separation unclear", "Offsite copies, not isolated", "Isolated copies maintained", "Immutable, isolated, recovery tested"],
    control: "Ransomware-resilient backup isolation",
    risk: "Modern ransomware deliberately targets backups first. If yours are reachable from the production environment, you may be left with nothing to restore from.",
    action: "Maintain immutable, isolated backup copies that cannot be reached or altered from production systems.",
  },
  {
    id: "Q12", area: "personalData", weight: 2, screen: 4,
    question: "How well do you understand the personal data you hold, why you hold it, and when it is deleted?",
    options: ["Never mapped; nothing deleted", "Rough understanding only", "Main holdings known", "Documented, with retention schedule", "Mapped, reviewed, deletion automated"],
    control: "Personal data inventory, purpose & retention",
    risk: "The DPDP Act requires organisations to know what personal data they hold and why, and not to retain it indefinitely without purpose. Unmapped data cannot be protected or justified to a regulator, and excess retention magnifies the impact of any breach.",
    action: "Map the personal data you hold and its purpose, define access, and enforce a retention and deletion schedule.",
  },
];

// ---------- Industry module: 2 questions, selected by sector ----------
export const INDUSTRY_MODULES = {
  "rbi": [
    { id: "F1", area: "industry", weight: 3,
      question: "To what extent does your board or ownership oversee cyber security?",
      options: ["Delegated entirely to IT", "Informed only on incidents", "Occasional management review", "Periodic board review", "Standing board item, named lead"],
      control: "Board-level cyber governance",
      risk: "The RBI expects a board-approved cyber security policy with accountability held at board level, not delegated entirely to IT.",
      action: "Establish board-level cyber oversight and adopt a board-approved security policy." },
    { id: "F2", area: "industry", weight: 3, verify: true,
      question: "Could a serious incident be reported to the RBI within the required hours — and is independent security testing performed?",
      options: ["Neither in place", "Both would be difficult", "Reporting known; testing occasional", "Both met annually", "Tested process plus regular audit"],
      control: "Regulatory reporting & independent assurance",
      risk: "RBI frameworks require incident notification within hours — running in parallel with CERT-In's 6-hour obligation — alongside mandatory vulnerability testing and independent information systems audit.",
      action: "Establish and rehearse a regulatory reporting workflow, and schedule regular VAPT and IS audit with a CERT-In empanelled auditor." },
  ],
  "sebi": [
    { id: "S1", area: "industry", weight: 3,
      question: "Is cyber resilience treated as a board-level responsibility?",
      options: ["Delegated entirely to IT", "Rarely discussed", "Occasional management review", "Regular leadership review", "Standing board responsibility, named lead"],
      control: "Board-level cyber resilience governance",
      risk: "SEBI's Cybersecurity and Cyber Resilience Framework places significant emphasis on governance, accountability and named ownership.",
      action: "Establish board-level cyber resilience ownership with a named security lead." },
    { id: "S2", area: "industry", weight: 3, verify: true,
      question: "Could critical operations withstand a serious attack — and be reported to SEBI/CERT-In in time?",
      options: ["Neither in place", "Would struggle", "Untested on both", "Plan in place, reporting feasible", "Tested resilience, rehearsed reporting"],
      control: "Operational resilience & regulatory reporting",
      risk: "CSCRF is structured around five resilience goals — Anticipate, Withstand, Contain, Recover, Evolve — with reporting deadlines running parallel to CERT-In's 6-hour requirement.",
      action: "Build and test a cyber resilience plan against CSCRF goals, supported by a rehearsed reporting workflow." },
  ],
  "retail": [
    { id: "R1", area: "industry", weight: 3, verify: true,
      question: "Where do customer card details go — and are full card numbers ever retained?",
      options: ["Handled and stored in-house", "Pass through own systems; some retained", "Uncertain", "Mostly via provider; none believed stored", "Provider-only; confirmed none stored"],
      control: "Cardholder data scope",
      risk: "PCI DSS applies wherever card data is stored, processed or transmitted. Retaining full card numbers places a business in the highest obligation tier and is the most common root cause of serious card breaches.",
      action: "Route all card payments through a compliant payment provider so card data never enters your systems, and confirm nothing is retained." },
    { id: "R2", area: "industry", weight: 2, verify: true,
      question: "If your online store were compromised, would stored customer data be protected?",
      options: ["Would be exposed", "Likely exposed", "Partial protection, unverified", "Sensitive data protected", "Protected, encrypted, tested"],
      control: "Customer data protection & encryption",
      risk: "PCI DSS requires encryption of sensitive data and prohibits default credentials; unprotected customer data turns a website compromise into a full-scale breach.",
      action: "Encrypt stored customer data, eliminate default credentials, and test the storefront regularly." },
  ],
  "healthcare": [
    { id: "H1", area: "industry", weight: 3, verify: true,
      question: "How are patient records held, and how is access to them controlled?",
      options: ["No protection or access control", "Basic protection, limited control", "Protected, some control", "Protected, role-based access", "Protected, role-based, logged and reviewed"],
      control: "Health record protection & access governance",
      risk: "Health information is among the most sensitive categories of personal data under the DPDP Act. Uncontrolled access to patient records constitutes both a privacy failure and a regulatory exposure.",
      action: "Apply strong protection with role-based access control, logging and periodic access review." },
    { id: "H2", area: "industry", weight: 2,
      question: "Are patient card payments handled entirely by a payment provider?",
      options: ["Handled and retained in-house", "Through own system", "Uncertain", "Mostly via provider", "Provider-only; no card data held"],
      control: "Payment data scope in clinical billing",
      risk: "Healthcare billing combines payment data with health records, compounding breach impact and bringing PCI DSS into scope.",
      action: "Transfer all card handling to a compliant payment provider so card data never enters clinical systems." },
  ],
  "it": [
    { id: "I1", area: "industry", weight: 2,
      question: "Do customers request security assurance — and can you provide it?",
      options: ["Unfamiliar with these", "Asked; unable to evidence", "Asked; working towards it", "Partial evidence available", "Certified or near-certified"],
      control: "Certification & assurance readiness",
      risk: "Enterprise buyers increasingly require recognised certification before contracting; without it, deals stall and each sale carries a bespoke security review.",
      action: "Initiate a structured ISO 27001 / SOC 2 readiness programme." },
    { id: "I2", area: "industry", weight: 3, verify: true,
      question: "Do you hold customer data on their behalf — and how is it protected and separated?",
      options: ["Minimal protection or separation", "Basic protection", "Protected; separation unclear", "Protected and separated per customer", "Protected, separated, monitored, tested"],
      control: "Customer data custody & tenant isolation",
      risk: "Acting as a processor of others' data means inheriting their compliance obligations, and a single failure can affect every customer simultaneously.",
      action: "Strengthen tenant separation, encryption and monitoring of customer-held data." },
  ],
  "professional": [
    { id: "P1", area: "industry", weight: 3, verify: true,
      question: "Could confidential client material be accessed by someone without a need to see it?",
      options: ["Open to all staff", "Likely", "Partially controlled", "Access restricted appropriately", "Restricted, logged and reviewed"],
      control: "Client confidentiality & access segregation",
      risk: "Unrestricted file access risks cross-client disclosure, conflicts of interest, and breach of professional confidentiality obligations.",
      action: "Restrict access by client and role, with logging and periodic review." },
    { id: "P2", area: "industry", weight: 2,
      question: "How is client material shared?",
      options: ["Email attachments", "Mostly email", "Mixed methods", "Usually secure platform", "Always secure, controlled platform"],
      control: "Secure information exchange",
      risk: "Email is readily intercepted or misdirected and remains a leading cause of confidential client data loss.",
      action: "Adopt a secure client file-sharing platform and phase out email attachments for confidential material." },
  ],
  "manufacturing": [
    { id: "M1", area: "industry", weight: 3,
      question: "What operational impact would a cyber incident have on production?",
      options: ["Complete stoppage", "Extended disruption", "Significant slowdown", "Key operations maintained", "Continuity planned and tested"],
      control: "Operational continuity",
      risk: "Production downtime from a cyber incident carries immediate, measurable financial loss alongside contractual and reputational consequences.",
      action: "Develop and test operational continuity arrangements for production systems." },
    { id: "M2", area: "industry", weight: 3, verify: true,
      question: "Are production systems separated from the corporate network?",
      options: ["Single flat network", "Largely unseparated", "Partially separated", "Separated and protected", "Separated, protected, monitored"],
      control: "OT/IT network segregation",
      risk: "A flat network allows a single phishing email in the office environment to reach and halt production systems.",
      action: "Segregate operational technology from the corporate network and monitor the boundary." },
  ],
};

export const NOT_SURE_LABEL = "Not sure — would need to check";

// Resolve which industry module applies from the profile answers.
export function resolveModuleKey(profile) {
  const sector = profile.A1;
  if (sector === "Manufacturing / industrial") return "manufacturing";
  if (sector === "Financial services / lending") return profile.A1b === "Yes" ? "sebi" : "rbi";
  if (sector === "Healthcare") return "healthcare";
  if (sector === "Retail / e-commerce") return "retail";
  if (sector === "Professional services") return "professional";
  if (sector === "IT / software / SaaS") return "it";
  return "other"; // no module
}

export const TIERS = [
  { min: 85, tier: "Optimized", label: "Leading", color: "var(--tcc-ok)" },
  { min: 65, tier: "Managed", label: "Strong", color: "#5FB33C" },
  { min: 45, tier: "Defined", label: "Developing", color: "var(--tcc-warning)" },
  { min: 25, tier: "Emerging", label: "Basic", color: "var(--tcc-orange)" },
  { min: 0, tier: "Initial", label: "Exposed", color: "var(--tcc-danger)" },
];

export const READINESS_BANDS = [
  { min: 85, label: "Audit-ready" },
  { min: 65, label: "Largely ready" },
  { min: 45, label: "Progressing" },
  { min: 25, label: "Early stage" },
  { min: 0, label: "Not ready" },
];

export const FIRM = {
  name: "Techmate Infosec Solutions",
  address: "693, Shivajinagar, Pune – 411016",
  phone: "+91-9764796333",
  email: "info@techmatesolution.net",
  logo: "https://customer-assets-jt897jd0.emergentagent.net/job_techmate-security/artifacts/b9qrppi3_Techmate%20Logo.png",
};
